import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { finalize } from 'rxjs';
import { ID } from '@datorama/akita';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { ApplicationService } from '@state/application';
import { DocumentService, EditorState, FormService as LegacyFormService } from '@app/shared/services';
import {
  ElementObjectType,
  FormDisplayType,
  FormElement,
  FormRepository,
  FormSection,
  FormService,
  SectionType,
  WidgetType
} from '@state/form';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-builder',
  styleUrls: ['./builder.component.scss'],
  templateUrl: './builder.component.html'
})
export class BuilderComponent implements OnInit {
  @Input() id: string;
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDropElement: EventEmitter<any> = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onDropSection: EventEmitter<any> = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onRemoveElement: EventEmitter<any> = new EventEmitter<any>();
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onRemoveSection: EventEmitter<any> = new EventEmitter<any>();

  @Input() selectedItem: FormElement | FormSection;
  @Output() createSection = new EventEmitter();

  formHasNonGlobalVariables$ = this.formRepository.formHasNonGlobalVariables$;

  private REQUIREABLE_FIELDS = [
    'text_input',
    'text_area',
    'password',
    'multi_select',
    'custom',
    'date_picker',
    'document',
    'number_input',
    'radio'
  ];

  readonly WIDGET_TYPES = {
    checkbox: { icon: 'check_box', title: 'Checkbox' },
    content: { icon: 'code', title: 'Content Block' },
    custom: { icon: 'dashboard', title: 'Custom Select' },
    date_picker: { icon: 'event', title: 'Date Picker' },
    document: { icon: 'cloud_upload', title: 'Document Upload' },
    fixed_text: { icon: 'format_shapes', title: 'Fixed Text' },
    form: { icon: 'note_add', title: 'Form' },
    hidden: { icon: 'visibility_off', title: 'Hidden Input' },
    multi_select: { icon: 'arrow_drop_down_circle', title: 'Dropdown' },
    number_input: { icon: 'money', title: 'Number Input' },
    password: { icon: 'more_horiz', title: 'Password' },
    radio: { icon: 'radio_button_checked', title: 'Radio Group' },
    signature: { icon: 'gesture', title: 'Signature Block' },
    slide_toggle: { icon: 'toggle_on', title: 'Slide Toggle' },
    text_area: { icon: 'notes', title: 'Multiline Input' },
    text_input: { icon: 'text_format', title: 'Text Input' }
  };

  actorRoleIcons = {
    counter_1: { icon: 'person', tooltip: 'Counter Signatory 1 (GP)' },
    counter_2: { icon: 'people', tooltip: 'Counter Signatory 2 (GP)' },
    counter_3: { icon: 'people', tooltip: 'Counter Signatory 3 (GP)' },
    counter_4: { icon: 'people', tooltip: 'Counter Signatory 4 (GP)' },
    counter_5: { icon: 'people', tooltip: 'Counter Signatory 5 (GP)' },
    counter_6: { icon: 'people', tooltip: 'Counter Signatory 6 (GP)' },
    primary_1: { icon: 'person', tooltip: 'Primary Signatory 1' },
    primary_2: { icon: 'people', tooltip: 'Primary Signatory 2' },
    primary_3: { icon: 'people', tooltip: 'Primary Signatory 3' },
    primary_4: { icon: 'people', tooltip: 'Primary Signatory 4' },
    primary_5: { icon: 'people', tooltip: 'Primary Signatory 5' },
    primary_6: { icon: 'people', tooltip: 'Primary Signatory 6' }
  };

  content: string;
  sectionIdRefs = [];
  readonly SectionType = SectionType;
  readonly WidgetType = WidgetType;

  constructor(
    private applicationService: ApplicationService,
    private formRepository: FormRepository,
    private formService: FormService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    public documentService: DocumentService,
    public editorState: EditorState,
    public legacyFormService: LegacyFormService
  ) {}

  ngOnInit() {
    this.setSectionIdRefs();
  }

  get canAddSpecialSection(): boolean {
    let { assignable_to, display_type } = this.legacyFormService.form;

    return (
      display_type === FormDisplayType.Wizard &&
      (assignable_to === 'investment' || assignable_to === 'vehicle') &&
      this.hasAnAvailableSection
    );
  }

  get hasAnAvailableSection(): boolean {
    return (
      !this.hasInvestmentAccountSelectionForm ||
      !this.hasInvestmentAccountSummary ||
      !this.hasInvestorsForm ||
      !this.hasTeamForm
    );
  }

  get hasInvestmentAccountSelectionForm(): boolean {
    return this.hasSection(SectionType.InvestmentAccountSelection);
  }

  get hasInvestmentAccountSummary(): boolean {
    return this.hasSection(SectionType.InvestmentAccountSummary);
  }

  get hasTeamForm(): boolean {
    return this.hasSection(SectionType.Team);
  }

  get hasInvestorsForm(): boolean {
    return this.hasSection(SectionType.Investors);
  }

  get hasProviderAccessRequest(): boolean {
    return this.hasSection(SectionType.ProviderAccessRequest);
  }

  hasSection(type: SectionType): boolean {
    return this.legacyFormService.form.sections?.some((section) => section.type === type);
  }

  setSectionIdRefs(): void {
    this.legacyFormService.form.sections.forEach((section) => {
      this.sectionIdRefs.push(section.id);
    });
  }

  removeElement(section: FormSection, element: FormElement): void {
    let params = { element, section };
    this.onRemoveElement.emit(params);
  }

  removeSection(section: FormSection): void {
    if (this.legacyFormService.form.sections?.length > 1) {
      let params = { section };
      this.onRemoveSection.emit(params);
    } else {
      this.snackBar.open(`You cannot delete the last section that exists in a form`, '', {
        panelClass: 'snack-error'
      });
    }
  }

  repairCustomVariables(): void {
    let form_id = this.formRepository.getFormId();
    this.applicationService.pushLoading('builder/repair-custom-variables');
    this.formService
      .repairCustomVariables$(form_id)
      .pipe(
        finalize(() => {
          this.applicationService.popLoading('builder/repair-custom-variables');
        })
      )
      .subscribe({
        next: () => window.location.reload()
      });
  }

  isObjectSelected(object: FormSection | FormElement): boolean {
    return this.editorState.selectedItem && this.editorState.selectedItem.id === object.id;
  }

  dropElement(event: CdkDragDrop<FormSection>): void {
    if (!this.legacyFormService.editable) return;

    let originalSection: any = this.legacyFormService.form.sections.find(
      (section) => event.previousContainer.id === section.id
    );

    if (event.previousContainer === event.container) {
      this.transferElementWithinSection(event, originalSection);
    } else {
      this.transferElementAcrossSections(event, originalSection);
    }
  }

  transferElementAcrossSections(event: CdkDragDrop<FormSection>, originalSection: FormSection): void {
    let newSection: any;
    if (event.previousContainer !== event.container) {
      newSection = this.legacyFormService.form.sections.find((section) => event.container.id === section.id);
    }

    transferArrayItem(originalSection.elements, newSection.elements, event.previousIndex, event.currentIndex);

    this.updatePositions(originalSection.elements, originalSection.id);
    this.updatePositions(newSection.elements, newSection.id);

    this.legacyFormService.putElements(originalSection.id, originalSection.elements, null, false);

    this.legacyFormService.putElements(newSection.id, newSection.elements, null, false);
  }

  transferElementWithinSection(event: CdkDragDrop<FormSection>, originalSection: FormSection): void {
    moveItemInArray(originalSection.elements, event.previousIndex, event.currentIndex);

    this.updatePositions(originalSection.elements, originalSection.id);

    let elementsToUpdate = originalSection.elements.map((elem) => ({ id: elem.id, position: elem.position }));
    this.legacyFormService.sortElements(originalSection.id, elementsToUpdate, null, false);
  }

  moveElement(element: FormElement, section: FormSection, newPosition: number): void {
    let originalSection = this.getOriginalSection(section.id);
    let position = element.position;

    moveItemInArray(originalSection.elements, position, newPosition);

    this.updatePositions(originalSection.elements, originalSection.id);

    this.legacyFormService.putElements(originalSection.id, originalSection.elements, null, false);
  }

  moveElementTowards(element: FormElement, section: FormSection, stepAndDirection: number): void {
    let position = element.position;
    this.moveElement(element, section, position + stepAndDirection);
  }

  moveElementToTop(element: any, section: any): void {
    this.moveElement(element, section, 0);
  }

  moveElementToBottom(element: FormElement, section: FormSection): void {
    let originalSection = this.getOriginalSection(section.id);

    this.moveElement(element, section, originalSection.elements.length);
  }

  getOriginalSection(sectionId: ID): FormSection {
    return this.legacyFormService.form.sections.find((section: FormSection) => sectionId === section.id);
  }

  addSection(sectionType?: SectionType): void {
    if (!this.legacyFormService.editable) return;

    let type = sectionType ? sectionType : SectionType.Standard;

    this.legacyFormService.addSection({
      callbacks: {
        success: () => this.setSectionIdRefs()
      },
      section: { type }
    });
  }

  dropSection(event: CdkDragDrop<string[]>): void {
    if (!this.legacyFormService.editable) return;

    moveItemInArray(this.legacyFormService.form.sections, event.previousIndex, event.currentIndex);
    this.updatePositions(this.legacyFormService.form.sections);
    this.onDropSection.emit(this.legacyFormService.form.sections);

    let sectionsToUpdate = this.legacyFormService.form.sections.map((section) => ({
      id: section.id,
      position: section.position
    }));
    this.legacyFormService.sortSections(sectionsToUpdate, null, false);
  }

  moveSection(section: FormSection, newPosition: number): void {
    if (!this.legacyFormService.editable) return;

    let position = section.position;

    moveItemInArray(this.legacyFormService.form.sections, position, newPosition);
    this.updatePositions(this.legacyFormService.form.sections);
    this.onDropSection.emit(this.legacyFormService.form.sections);
  }

  moveSectionTowards(section: FormSection, stepAndDirection: number): void {
    let position = section.position;
    this.moveSection(section, position + stepAndDirection);
  }

  moveSectionToTop(section: FormSection): void {
    this.moveSection(section, 0);
  }

  moveSectionToBottom(section: FormSection): void {
    let originalSection = this.getOriginalSection(section.id);

    this.moveSection(section, originalSection.elements.length);
  }

  updatePositions(formElements: FormElement[] | FormSection[], sectionId?: ID): void {
    formElements.forEach((formElement, index: number) => {
      if (sectionId && formElement.form_section_id !== sectionId) {
        formElement.form_section_id = sectionId;
      }

      formElement.position = index;
    });
  }

  setElementSectionId(section: FormSection): void {
    this.legacyFormService.elementSectionId = section.id;
  }

  addElement(section: FormSection, widget_type: WidgetType): void {
    if (!this.legacyFormService.editable) return;

    this.legacyFormService.addElement(section, widget_type);
  }

  addContent(section: FormSection): void {
    if (!this.legacyFormService.editable) return;

    this.legacyFormService.addElement(section, WidgetType.Content);
  }

  isRequired(element: FormElement): boolean {
    return (
      element.element_object_type === ElementObjectType.FormField &&
      element.element_object.required &&
      this.REQUIREABLE_FIELDS.includes(element.element_object.widget_type)
    );
  }

  isElementCounterSignable(element: FormElement): boolean {
    return element.role.includes('counter');
  }

  isElementFixedText(element: FormElement): boolean {
    return (
      element.element_object_type === ElementObjectType.FormField &&
      element.element_object.widget_type === WidgetType.FixedText
    );
  }

  isElementConditionalVisible(item: FormElement | FormSection): string {
    return item.visibility_key;
  }
}
